import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateNotificationTemplateDto,
  PreviewNotificationTemplateDto,
  UpdateNotificationTemplateDto,
} from '@control-center/shared';

import {
  createTemplate,
  fetchTemplateById,
  fetchTemplates,
  previewTemplate,
  updateTemplate,
  type TemplateListFilter,
} from '../lib/templates-api';

export const templatesKeys = {
  all: ['notification-templates'] as const,
  list: (filter: TemplateListFilter) => [...templatesKeys.all, 'list', filter] as const,
  detail: (id: string) => [...templatesKeys.all, 'detail', id] as const,
};

export function useTemplates(filter: TemplateListFilter = {}) {
  return useQuery({
    queryKey: templatesKeys.list(filter),
    queryFn: () => fetchTemplates(filter),
    staleTime: 60_000,
  });
}

export function useTemplate(id: string | null) {
  return useQuery({
    queryKey: id ? templatesKeys.detail(id) : ['notification-templates', 'detail', 'noop'],
    queryFn: () => fetchTemplateById(id as string),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateNotificationTemplateDto) => createTemplate(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: templatesKeys.all }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateNotificationTemplateDto }) =>
      updateTemplate(id, dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: templatesKeys.all }),
  });
}

export function usePreviewTemplate() {
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: PreviewNotificationTemplateDto }) =>
      previewTemplate(id, dto),
  });
}
